import { EProcessNames } from "api/enums";
import AccessProfileModel from "api/models/AccessProfileModel";
import AccessProfileService from "api/services/AccessProfileService";
import UserService from "api/services/UserService";
import { Unsubscribe } from "firebase/firestore";
import { lang } from "i18nLang";
import { makeAutoObservable } from "mobx";
import { compareStrings } from "utils";
import CompanyStore from "./CompanyStore";
import MessageStore from "./MessageStore";
import UserStore from "./UserStore";

class Store {
  public accessProfiles: AccessProfileModel[] = [];
  public userAccesProfile?: AccessProfileModel;
  private accessProfileObserver?: Unsubscribe;

  constructor() {
    makeAutoObservable(this);
  }

  reset() {
    this.accessProfiles = [];
  }

  public async getAndSetCurrentUserAccessProfile() {
    const company = CompanyStore.currentCompany;
    const user = UserStore.currentUser;
    if (!company || !user) return;

    const profile = await AccessProfileService.getAccessProfileByUid(
      company.uid,
      user.roleId || ""
    );
    this.userAccesProfile = profile;
  }

  public currentUserIsAdmin() {
    if (!this.userAccesProfile) return false;
    return this.userAccesProfile.isAdmin();
  }

  public validateAccessPermissionTo(process: EProcessNames): boolean {
    if (!this.userAccesProfile) return false;
    if (this.userAccesProfile.isAdmin()) return true;
    return this.userAccesProfile.permissions.includes(process);
  }

  public validateProfileName(profileName: string, excludeUid: string) {
    if (AccessProfileStore.hasProfileWithSameName(profileName, excludeUid)) {
      return {
        valid: false,
        text: lang("userProfile.thisProfileAlreadyExists"),
      };
    }
    return;
  }

  private hasProfileWithSameName(profileName: string, excludeUid: string) {
    return this.accessProfiles.some(
      (profile) =>
        profile.name.toLowerCase() === profileName.toLowerCase() &&
        profile.uid !== excludeUid
    );
  }

  async saveAccessProfile(
    uid: string,
    profileName: string,
    permissions: string[]
  ) {
    const companyUid = CompanyStore.currentCompany?.uid;
    if (!companyUid) return;

    if (uid) {
      return await AccessProfileService.updateAccessProfile(companyUid, uid, {
        name: profileName,
        permissions,
      })
        .then(() =>
          MessageStore.success(lang("userProfile.profileEditedSuccessfully"))
        )
        .catch((error) => {
          MessageStore.error(
            lang("userProfile.errorEditingProfile", error.message)
          );
          throw error;
        });
    }

    await AccessProfileService.addAccessProfile(companyUid, {
      name: profileName,
      permissions,
    })
      .then(() =>
        MessageStore.success(lang("userProfile.profileCreatedSuccessfully"))
      )
      .catch((error) => {
        MessageStore.error(
          lang("userProfile.errorCreatingProfile", error.message)
        );
        throw error;
      });
  }

  public async deleteAccessProfile(
    profile: AccessProfileModel
  ): Promise<boolean> {
    const companyUid = CompanyStore.currentCompany?.uid;
    if (!companyUid) return false;

    const users = await UserService.getUsersByRoleUid(profile.uid);
    if (users.length) {
      MessageStore.error(lang("userProfile.profileCannotBeDeleted"));
      return false;
    }

    return AccessProfileService.deleteAccessProfile(companyUid, profile.uid)
      .then(() => {
        MessageStore.success(lang("userProfile.profileDeletedSuccessfully"));
        return true;
      })
      .catch((error) => {
        MessageStore.error(
          lang("userProfile.errorDeletingProfile", error.message)
        );
        return false;
      });
  }

  public subscribeAccessProfiles() {
    this.reset();

    const companyUid = CompanyStore.currentCompany?.uid;
    if (!companyUid) return;

    this.accessProfileObserver =
      AccessProfileService.subscribeAccessProfilesByCompany(
        companyUid,
        (profilesAdded) => this.addAccessProfilesToList(profilesAdded),
        (profilesChanged) => this.changeAccessProfilesOnList(profilesChanged),
        (profilesRemoved) => this.removeAccessProfilesFromList(profilesRemoved)
      );
  }

  private addAccessProfilesToList(profilesAdded: AccessProfileModel[] = []) {
    this.setQuantityUserOfProfiles(profilesAdded);
    this.accessProfiles = profilesAdded
      .concat(this.accessProfiles)
      .sort((a, b) => compareStrings(a.name, b.name));
  }

  private changeAccessProfilesOnList(
    profilesChanged: AccessProfileModel[] = []
  ) {
    const accessProfiles = this.accessProfiles;
    this.setQuantityUserOfProfiles(profilesChanged);
    profilesChanged.forEach((prof) => {
      const index = accessProfiles.findIndex((p) => p.uid === prof.uid);
      if (index !== -1) accessProfiles[index] = prof;
    });
    this.accessProfiles = accessProfiles.sort((a, b) =>
      compareStrings(a.name, b.name)
    );
  }

  private removeAccessProfilesFromList(
    profilesRemoved: AccessProfileModel[] = []
  ) {
    const uids = profilesRemoved.map((prof) => prof.uid);
    this.accessProfiles = this.accessProfiles.filter(
      (prof) => !uids.includes(prof.uid)
    );
  }

  private setQuantityUserOfProfiles(profiles: AccessProfileModel[]) {
    const users = CompanyStore.employeesList;

    profiles.forEach((profile) => {
      let qttUsers = users.filter((user) => user.roleId === profile.uid).length;
      if (UserStore.currentUser?.roleId === profile.uid) {
        qttUsers++;
      }
      profile.setQuantityUsers(qttUsers);
    });
  }

  public unsubscribeAccessProfiles() {
    if (this.accessProfileObserver) {
      this.accessProfileObserver();
    }
  }
}

const AccessProfileStore = new Store();
export default AccessProfileStore;
